import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { getFullApiUrl } from "../../config";
import { formatDateSlash, getToken } from "../../utils";
import { Badge, Button, Dialog } from "../../designSystem";
import Notice from "../ui/Notice";
import { LoadingPage } from "../LoadingPage";

const SubscriptionManagePage = () => {
  const [paymentItems, setPaymentItems] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [zsxqBinding, setZsxqBinding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(null);
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchPaymentInfo(), fetchPaymentStatus(), fetchZsxqBinding()]);
    setLoading(false);
  };

  const fetchZsxqBinding = async () => {
    try {
      const token = getToken();
      const response = await axios.get(getFullApiUrl("/api/v1/auth/zsxq_binding"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setZsxqBinding(response.data);
    } catch (err) {
      console.error("Failed to fetch zsxq binding:", err);
      setZsxqBinding(null);
    }
  };

  const fetchPaymentInfo = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        getFullApiUrl("/api/v1/stripe/user-payment-info"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setPaymentItems(response.data.payment_items || []);
    } catch (err) {
      console.error("Failed to fetch payment info:", err);
      setError("无法获取付费信息");
    }
  };

  const fetchPaymentStatus = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        getFullApiUrl("/api/v1/subscribe/user_payment_status"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setPaymentStatus(response.data);
    } catch (err) {
      console.error("Failed to fetch payment status:", err);
      // Handle authentication errors
      if (err.response?.status === 401) {
        // Token expired or invalid, redirect to login
        navigate("/login");
        return;
      }
      // For other errors, set a default state
      setPaymentStatus({ has_paid: false });
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    setProcessing(true);
    try {
      const token = getToken();
      await axios.post(
        getFullApiUrl(`/api/v1/stripe/cancel-subscription/${subscriptionId}`),
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Refresh payment info
      await fetchPaymentInfo();
      setCancelDialogOpen(false);
      setCancellingSubscription(null);
    } catch (err) {
      console.error("Failed to cancel subscription:", err);
      setError(err.response?.data?.error || "取消订阅失败");
    } finally {
      setProcessing(false);
    }
  };

  const statusBadge = (status) => {
    switch (status) {
      case "active":
        return (
          <Badge variant="free" icon="check_circle">
            有效
          </Badge>
        );
      case "trialing":
        return (
          <Badge variant="video" icon={null}>
            试用中
          </Badge>
        );
      case "canceled":
        return (
          <Badge variant="neutral" icon={null}>
            已取消
          </Badge>
        );
      case "past_due":
        return (
          <Badge variant="member" icon="warning">
            逾期
          </Badge>
        );
      case "incomplete":
        return (
          <Badge variant="neutral" icon={null}>
            未完成
          </Badge>
        );
      case "succeeded":
        return (
          <Badge variant="free" icon="check_circle">
            已购买
          </Badge>
        );
      default:
        return (
          <Badge variant="neutral" icon={null}>
            {status}
          </Badge>
        );
    }
  };

  const formatPrice = (amount, currency) => {
    const price = (amount / 100).toFixed(2);
    const currencySymbol =
      currency === "usd" ? "$" : currency === "cny" ? "¥" : currency.toUpperCase();
    return `${currencySymbol}${price}`;
  };

  const openCancelDialog = (item) => {
    setCancellingSubscription(item);
    setCancelDialogOpen(true);
  };

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <div className="lt-main" style={{ maxWidth: 760 }}>
      <div className="lt-pagehead">
        <h1>订阅管理</h1>
      </div>

      {error ? <Notice variant="error" style={{ marginTop: 8 }}>{error}</Notice> : null}

      {paymentStatus ? (
        <div className="lt-set-section">
          <h3>付费状态</h3>
          <div className="lt-card">
            <div className="lt-set-row">
              <span className="t">
                <b>{paymentStatus.has_paid ? "已付费" : "免费收听中"}</b>
                <p>
                  {paymentStatus.has_paid
                    ? [
                        {
                          stripe: "通过 Stripe 付费",
                          zsxq: "通过知识星球付费",
                          apple: "通过 App Store 付费",
                          manual: "由管理员开通",
                          combined: "多渠道会员时长叠加",
                        }[paymentStatus.payment_source] || "已付费",
                        paymentStatus.expires_at
                          ? `，会员有效期至 ${formatDateSlash(paymentStatus.expires_at)}（各渠道时长叠加计算）`
                          : "",
                      ].join("")
                    : "成为会员，解锁全部付费节目"}
                </p>
              </span>
              <span className="r">
                {paymentStatus.has_paid ? (
                  <Badge variant="member" icon="verified">
                    生效中
                  </Badge>
                ) : (
                  <Button size="sm" onClick={() => navigate("/subscription/plans")}>
                    成为会员
                  </Button>
                )}
              </span>
            </div>
            {paymentStatus.has_paid &&
            paymentStatus.membership_periods?.length > 0
              ? paymentStatus.membership_periods.map((p, index) => {
                  const label =
                    {
                      stripe: "Stripe",
                      zsxq: "知识星球",
                      apple: "App Store",
                      manual: "管理员开通",
                    }[p.source] || p.source;
                  const days = Math.round(
                    (new Date(p.end_at) - new Date(p.start_at)) / 86400000
                  );
                  return (
                    <div
                      className="lt-set-row"
                      key={`${p.source}-${p.start_at}-${index}`}
                    >
                      <span className="t">
                        <b style={{ fontSize: "var(--text-sm)" }}>{label}</b>
                        <p>
                          {formatDateSlash(p.start_at)} —{" "}
                          {formatDateSlash(p.end_at)} · {days} 天
                        </p>
                      </span>
                    </div>
                  );
                })
              : null}
          </div>
        </div>
      ) : null}

      {paymentItems.length > 0 ? (
        <div className="lt-set-section">
          <h3>付费记录</h3>
          <div className="lt-card">
            {paymentItems.map((item) => (
              <div className="lt-set-row" key={item.id}>
                <span className="t">
                  <b>
                    {item.type === "subscription"
                      ? `订阅 #${item.stripe_subscription_id?.slice(-6)}`
                      : `一次性购买 #${item.id.slice(-6)}`}
                  </b>
                  <p>
                    {item.type === "subscription" ? "当前周期" : "有效期"}：
                    {formatDateSlash(item.start_date)} — {formatDateSlash(item.end_date)}
                    {item.type === "purchase"
                      ? ` · ${formatPrice(item.amount, item.currency)}`
                      : ""}
                  </p>
                  {item.type === "subscription" && item.cancel_at_period_end ? (
                    <p style={{ color: "var(--accent-strong)" }}>
                      订阅已取消，会员身份保留到 {formatDateSlash(item.end_date)}
                    </p>
                  ) : null}
                </span>
                <span className="r">
                  {statusBadge(item.status || "succeeded")}
                  {item.type === "subscription" &&
                  item.status === "active" &&
                  !item.cancel_at_period_end ? (
                    <Button variant="ghost" size="sm" onClick={() => openCancelDialog(item)}>
                      取消订阅
                    </Button>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        !paymentStatus?.has_paid && (
          <div className="lt-set-section">
            <h3>付费记录</h3>
            <div className="lt-card">
              <div className="lt-set-row">
                <span className="t">
                  <b>暂无付费订阅</b>
                  <p>购买后即可收听所有付费内容</p>
                </span>
                <span className="r">
                  <Button size="sm" onClick={() => navigate("/subscription/plans")}>
                    查看付费计划
                  </Button>
                </span>
              </div>
            </div>
          </div>
        )
      )}

      <div className="lt-set-section">
        <h3>知识星球</h3>
        <div className="lt-card">
          {zsxqBinding?.bound && zsxqBinding.bindings?.length > 0 ? (
            <>
              {zsxqBinding.bindings.map((b) => (
                <div className="lt-set-row" key={b.zsxqs_id}>
                  <span className="t">
                    <b>
                      {b.zsxq_name}（编号 {b.zsxq_number}）
                    </b>
                    <p>
                      星球会员有效期至 {formatDateSlash(b.membership_end_at)}
                      ；时长叠加计入总有效期
                    </p>
                  </span>
                  <span className="r">
                    {new Date(b.membership_end_at) > new Date() ? (
                      <Badge variant="member" icon="verified">
                        生效中
                      </Badge>
                    ) : (
                      <Badge variant="neutral" icon={null}>
                        已过期
                      </Badge>
                    )}
                  </span>
                </div>
              ))}
              <div className="lt-set-row">
                <span className="t">
                  <b>管理绑定</b>
                  <p>解除绑定，或再绑定一个知识星球账号继续叠加时长</p>
                </span>
                <span className="r">
                  <Button variant="secondary" size="sm" onClick={() => navigate("/zsxq")}>
                    管理绑定
                  </Button>
                </span>
              </div>
            </>
          ) : (
            <div className="lt-set-row">
              <span className="t">
                <b>知识星球会员</b>
                <p>已经在知识星球付费？绑定账号即可使用会员服务，与其他渠道的会员时长叠加计算</p>
              </span>
              <span className="r">
                <Button variant="secondary" size="sm" onClick={() => navigate("/zsxq")}>
                  绑定知识星球账号
                </Button>
              </span>
            </div>
          )}
        </div>
      </div>

      {paymentStatus && paymentStatus.has_paid ? (
        <div className="lt-set-section">
          <h3>付费用户群</h3>
          <div className="lt-card">
            <div className="lt-set-row">
              <span className="t">
                <b>加入会员微信群</b>
                <p>
                  添加微信号 <strong>anotherlambda</strong>{" "}
                  并附上注册邮箱，anotherlambda 会邀请你进群。
                </p>
              </span>
            </div>
          </div>
        </div>
      ) : null}

      <Dialog
        open={cancelDialogOpen}
        title="确认取消订阅？"
        onClose={() => setCancelDialogOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCancelDialogOpen(false)} disabled={processing}>
              再想想
            </Button>
            <Button
              variant="danger"
              loading={processing}
              onClick={() =>
                handleCancelSubscription(cancellingSubscription?.stripe_subscription_id)
              }
            >
              {processing ? "正在处理" : "确认取消"}
            </Button>
          </>
        }
      >
        <p style={{ color: "var(--text-2)", fontSize: "var(--text-sm)" }}>
          取消后不再扣款，订阅会在当前周期结束后停止，到期后将无法继续收听付费内容。
          {cancellingSubscription
            ? ` 当前周期结束时间：${formatDateSlash(cancellingSubscription.end_date)}。`
            : ""}
        </p>
      </Dialog>
    </div>
  );
};

export default SubscriptionManagePage;
